/**
 * 聊天区域组件
 * 
 * 显示消息列表和打字指示器
 */

import React, { useRef, useEffect } from 'react';
import { Zap } from 'lucide-react';
import type { ChatMessage, Character } from '../../../types';
import {
  TextBubble,
  NarrativeCard,
  SystemMessage,
  ThoughtMessage,
  GameTypingIndicator,
} from '../messages';
import { isFirstInGroup, isLastInGroup } from '../../../utils/messageHelpers';

interface ChatAreaProps {
  messages: ChatMessage[];
  charA: Character;
  charB: Character;
  isTyping: boolean;
  typingSpeaker: Character | null;
  loadingText: string;
}

export const ChatArea: React.FC<ChatAreaProps> = React.memo(({
  messages,
  charA,
  charB,
  isTyping,
  typingSpeaker,
  loadingText,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const renderMessage = (msg: ChatMessage, index: number) => {
    switch (msg.type) {
      case 'system':
        return <SystemMessage key={msg.id} message={msg} />;
      case 'narrative':
        return <NarrativeCard key={msg.id} message={msg} />;
      case 'thought':
        return <ThoughtMessage key={msg.id} message={msg} />;
      default:
        return (
          <TextBubble
            key={msg.id}
            message={msg}
            charA={charA}
            charB={charB}
            isFirstInGroup={isFirstInGroup(messages, index)}
            isLastInGroup={isLastInGroup(messages, index)}
          />
        );
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:px-8 no-scrollbar">
      <div className="max-w-2xl mx-auto">
        {messages.map(renderMessage)}

        {isTyping && typingSpeaker && (
          <GameTypingIndicator speaker={typingSpeaker} charA={charA} />
        )}

        {loadingText && (
          <div className="flex justify-center my-4">
            <span className="text-xs text-[#9CA3AF] animate-pulse flex items-center gap-1">
              <Zap size={10} className="text-[#FFB347]" /> {loadingText}
            </span>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
    </div>
  );
});

ChatArea.displayName = 'ChatArea';
